import React, { useEffect, useState } from "react";
import { Terminal, Quote, Flame, FileStack, ScanLine, Dna, ShieldCheck, CalendarClock, Linkedin, Facebook, ArrowRight, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/useI18n";

const S = (n) => `/showcase/${n}.webp`;

const STACK = [
  { img: S("photo_coldbrew"), badge: "Photo Post", tone: "text-rose-300 border-rose-500/40 bg-rose-500/10", aspect: "aspect-square", day: 5, plat: Facebook,
    caption: "Cold brew tastes flat when the beans are old.\n\nOurs ship 72 hours after roasting." },
  { img: S("spec"), badge: "Spec Breakdown", tone: "text-sky-300 border-sky-500/40 bg-sky-500/10", aspect: "aspect-square", day: 1, plat: Linkedin,
    caption: "Most coffee sits in a warehouse for weeks.\n\nOurs ships 72 hours after roasting." },
  { img: S("founder"), badge: "Founder Log", tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", aspect: "aspect-square", day: 2, plat: Facebook,
    caption: "We killed our best-selling 2kg bag.\n\nSales went up 18% in a month." },
  { img: S("photo_roaster"), badge: "Founder Log", tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", aspect: "aspect-square", day: 6, plat: Linkedin,
    caption: "We roast in 12kg batches, never more.\n\nWe taste every batch before it ships." },
  { img: S("hottake"), badge: "Hot Take", tone: "text-amber-300 border-amber-500/40 bg-amber-500/10", aspect: "aspect-square", day: 3, plat: Linkedin,
    caption: "Dark roast isn't stronger.\n\nCaffeine drops the longer you roast." },
  { img: S("slide1"), badge: "PDF Carousel", tone: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10", aspect: "aspect-[4/5]", day: 4, plat: Linkedin,
    caption: "3 numbers behind a better morning cup.\n\nSwipe the 5-slide breakdown." },
  { img: S("photo_desk"), badge: "Photo Post", tone: "text-rose-300 border-rose-500/40 bg-rose-500/10", aspect: "aspect-square", day: 7, plat: Linkedin,
    caption: "Your 9am meeting runs on a 1:16 brew ratio.\n\n20g coffee, 320g water. Tape it to the kettle." },
];

// Hero: the real engine output — rotating through the 4-day archetype rhythm.
export const HeroVisualStack = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % STACK.length), 3800); return () => clearInterval(t); }, []);
  const ex = STACK[i];
  const PIcon = ex.plat;
  return (
    <div className="glass rounded-2xl p-4 sm:p-5 relative" data-testid="hero-preview-card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0 bg-[#F97316]"><span className="font-display font-extrabold text-white text-sm">F</span></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight truncate" data-testid="hero-preview-brand">Fernwood Coffee</div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><PIcon className="h-3 w-3" /> Day {String(ex.day).padStart(2, "0")} of 30</div>
          </div>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-1 rounded-md border ${ex.tone}`} data-testid="hero-archetype-badge">{ex.badge}</span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
        <div className={`rounded-xl overflow-hidden border border-white/10 ${ex.aspect} mx-auto w-full ${ex.aspect === "aspect-square" ? "max-w-[400px]" : "max-w-[320px]"} bg-[#0D1117] shadow-2xl shadow-black/40`}>
          <img src={ex.img} alt={`${ex.badge} example`} className="w-full h-full object-cover" />
        </div>
        <div className="hidden sm:flex flex-col gap-1.5">
          {STACK.map((s, k) => (
            <button key={s.img} onClick={() => setI(k)} aria-label={s.badge}
              className={`h-11 w-11 rounded-lg overflow-hidden border transition-all ${k === i ? "border-primary ring-2 ring-primary/40" : "border-border opacity-50 hover:opacity-90"}`}>
              <img src={s.img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line mt-4">{ex.caption}</p>
      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground">
        <span><span className="font-mono uppercase tracking-wider text-emerald-400">Anchored on</span> · real specs from the brand's site</span>
        <span className="font-mono">0 emojis · 2 hashtags</span>
      </div>
    </div>
  );
};

const RHYTHM = [
  { k: "r1", day: "Day 1", name: "Spec Breakdown", visual: "Terminal spec card", icon: Terminal, img: S("spec"), photo: S("photo_bag"), aspect: "aspect-square",
    copy: "Problem, one concrete spec, a clear outcome." },
  { k: "r2", day: "Day 2", name: "Founder Log", visual: "High-contrast quote card", icon: Quote, img: S("founder"), photo: S("photo_roaster"), aspect: "aspect-square",
    copy: "Behind-the-scenes decisions with the real number." },
  { k: "r3", day: "Day 3", name: "Metric Hot Take", visual: "High-contrast quote card", icon: Flame, img: S("hottake"), photo: S("photo_coldbrew"), aspect: "aspect-square",
    copy: "Challenges common advice using your own data." },
  { k: "r4", day: "Day 4", name: "PDF Carousel", visual: "5-slide LinkedIn document", icon: FileStack, img: S("slide1"), photo: S("photo_desk"), aspect: "aspect-[4/5]",
    copy: "Swipeable on LinkedIn. Facebook gets the cover." },
];

const PIPE = [
  { k: "p1", icon: ScanLine, t: "Scan your website", d: "Clean page text, no meta-tag guessing" },
  { k: "p2", icon: Dna, t: "Extract Brand DNA", d: "Specs, real numbers, pains, buyer, voice" },
  { k: "p3", icon: ShieldCheck, t: "Code-checked copy", d: "Banned buzzwords, short hooks, every post cites a spec" },
  { k: "p4", icon: CalendarClock, t: "Render & schedule", d: "Cards + PDFs queued at Facebook & LinkedIn peak times" },
];

// The 4-day content rhythm + the Brand DNA pipeline, using the engine's real output.
export const RhythmSection = ({ onStart }) => {
  const { t } = useI18n();
  return (
  <section id="visual-engine" aria-label="Content rhythm" className="border-t border-border/60" data-testid="rhythm-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="max-w-2xl mb-12">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-3">{t("landing.ve_kicker", "The 30-day rhythm")}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("landing.ve_title", "Four post types. Cards and photos. Built from your real specs.")}</h2>
        <p className="text-base text-muted-foreground mt-4">{t("landing.ve_sub", "Every 3rd post is automatically an AI product photo, started from your real Shopify or website product image, so your feed stays varied. The rest are data cards and PDF carousels. Every caption cites a fact from your own website.")}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {RHYTHM.map((r, k) => {
          const Icon = r.icon;
          return (
            <div key={r.name} className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors animate-fadeUp"
              style={{ animationDelay: `${k * 0.08}s` }} data-testid={`rhythm-card-${k}`}>
              <div className="grid grid-cols-2 gap-px bg-border aspect-[2/1] w-full overflow-hidden">
                <div className="relative overflow-hidden bg-[#0D1117]">
                  <img src={r.img} alt={`${r.name} card`} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" />
                  <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/70 text-white/90">Card</span>
                </div>
                <div className="relative overflow-hidden">
                  <img src={r.photo} alt={`${r.name} photo`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/70 text-white/90">AI photo</span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                  <span>{t(`landing.ve_${r.k}_day`, r.day)}</span><span className="flex items-center gap-1"><Icon className="h-3.5 w-3.5 text-primary" /> {t(`landing.ve_${r.k}_visual`, r.visual)}</span>
                </div>
                <p className="font-semibold mt-2">{t(`landing.ve_${r.k}_name`, r.name)}</p>
                <p className="text-sm text-muted-foreground mt-1">{t(`landing.ve_${r.k}_copy`, r.copy)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 rounded-2xl border border-border bg-card/60 p-5 flex gap-3 overflow-x-auto" data-testid="carousel-strip">
        {["slide1", "slide2", "slide3", "slide5"].map((n) => (
          <img key={n} src={S(n)} alt="Carousel slide" loading="lazy" className="h-48 sm:h-56 aspect-[4/5] rounded-lg border border-border object-cover shrink-0" />
        ))}
        <div className="shrink-0 flex flex-col justify-center pl-2 max-w-[220px]">
          <p className="font-semibold text-sm">{t("landing.ve_car_title", "LinkedIn PDF carousels, built for you")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("landing.ve_car_sub", "Cover hook, 3 spec slides, CTA. Edit any slide, export the PDF or queue it.")}</p>
        </div>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dna-pipeline">
        {PIPE.map((p, k) => {
          const Icon = p.icon;
          return (
            <div key={p.t} className="relative rounded-xl border border-border p-4 bg-card/40">
              <span className="text-[10px] font-mono text-muted-foreground">{t("landing.ve_step", "STEP")} {k + 1}</span>
              <p className="font-semibold mt-1 flex items-center gap-2"><Icon className="h-4 w-4 text-emerald-400" /> {t(`landing.ve_${p.k}_t`, p.t)}</p>
              <p className="text-xs text-muted-foreground mt-1">{t(`landing.ve_${p.k}_d`, p.d)}</p>
              {k < PIPE.length - 1 && <ArrowRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />}
            </div>
          );
        })}
      </div>
      {onStart && (
        <button onClick={onStart} data-testid="rhythm-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
          {t("landing.ve_cta", "Start for $1 — your full 30-day campaign included")} <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  </section>
  );
};

// Public gallery of real, approved campaign posts from customers who opted in. Hidden until 3+ exist.
export const ShowcaseGallery = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/showcase?limit=9").then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);
  if (items.length < 3) return null;
  return (
    <section id="customer-showcase" aria-label="Customer posts" className="border-t border-border/60" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3 flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> {t("landing.sc_kicker", "Real customer posts")}</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">{t("landing.sc_title", "Approved by real brands. Unedited.")}</h2>
        <p className="text-base text-muted-foreground mt-4 max-w-2xl">{t("landing.sc_sub", "Posts from AutoGridSocial customers who chose to share their campaigns.")}</p>
        <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((it, k) => {
            const PIcon = it.platform === "linkedin" ? Linkedin : Facebook;
            return (
              <div key={it.id} className="mb-5 break-inside-avoid rounded-2xl border border-border bg-card overflow-hidden animate-fadeUp" style={{ animationDelay: `${(k % 6) * 0.06}s` }} data-testid={`showcase-item-${k}`}>
                <div className="flex items-center justify-between px-4 pt-4 pb-3 gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{it.brand}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><PIcon className="h-3 w-3" /> {it.published ? t("landing.sc_published", "Published") : t("landing.sc_scheduled", "Scheduled")}</p>
                  </div>
                  {it.badge && <span className="text-[10px] font-semibold px-2 py-1 rounded-md border border-border bg-secondary/40 shrink-0">{t(`landing.sc_badge_${it.badge}`, it.badge)}</span>}
                </div>
                <img src={it.image_url} alt={`${it.brand} post`} loading="lazy" className="w-full object-cover bg-[#0D1117]" />
                <p className="px-4 py-3 text-sm text-foreground/90 whitespace-pre-line leading-relaxed line-clamp-6">{it.post_text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
